<template>
  <layout v-if="dailyReport">
    <div class="page-break-around">
      <h2>{{ "daily_report" | translate }} #{{ dailyReport.id }}</h2>
      <h4>
        {{ "buyer" | translate }}: {{ dailyReport.buyer }} @
        {{ dailyReport.date }}
      </h4>

      <div v-no-print>
        <div class="mb-3">
          <a
            class="btn btn-primary mr-1"
            :href="`#/dailyReports/form?id=${dailyReportId}`"
            v-if="isEditable"
          >
            {{ "edit" | translate }}
          </a>
          <a href="#/dailyReports" class="btn btn-secondary mr-1">
            {{ "back" | translate }}
          </a>

          <button class="btn btn-outline-primary mr-1" @click="print">
            {{ "print" | translate }}
          </button>

          <button
            class="float-right btn btn-danger"
            @click="remove"
            v-if="isEditable"
          >
            {{ "delete" | translate }}
          </button>
        </div>
      </div>
      <div
        v-if="dailyReport.purchaseOrderItems.length"
        class="page-break-avoid"
      >
        <h3>{{ "purchase_orders" | translate }}</h3>

        <PartPurchaseOrderItemList
          :purchase-order-items="dailyReport.purchaseOrderItems"
          :readonly="true"
        ></PartPurchaseOrderItemList>
      </div>

      <div v-if="dailyReport.reserveOrderItems.length" class="page-break-avoid">
        <h3>{{ "reserve_orders" | translate }}</h3>

        <PartReserveOrderItemList
          :reserve-order-items="dailyReport.reserveOrderItems"
          :readonly="true"
        ></PartReserveOrderItemList>
      </div>

      <div v-if="dailyReport.refundOrderItems.length" class="page-break-avoid">
        <h3>{{ "refund_orders" | translate }}</h3>

        <PartRefundOrderItemList
          :refund-order-items="dailyReport.refundOrderItems"
          :readonly="true"
        ></PartRefundOrderItemList>
      </div>

      <div
        v-if="dailyReport.giftCardOrderItems.length"
        class="page-break-avoid"
      >
        <h3>{{ "gift_card_orders" | translate }}</h3>

        <PartGiftCardOrderItemList
          :gift-card-order-items="dailyReport.giftCardOrderItems"
          :readonly="true"
        ></PartGiftCardOrderItemList>
      </div>

      <div v-if="dailyReport.receiveItems.length" class="page-break-avoid">
        <div>
          <h3>{{ "receive_items" | translate }}</h3>

          <PartReceiveItemList
            :receive-items="dailyReport.receiveItems"
            :readonly="true"
          ></PartReceiveItemList>
        </div>
      </div>

      <div v-if="dailyReport.giveItems.length">
        <div>
          <h3>{{ "give_items" | translate }}</h3>

          <PartGiveItemList
            :give-items="dailyReport.giveItems"
            :readonly="true"
          ></PartGiveItemList>
        </div>
      </div>

      <div>
        <h3>{{ "account_summary" | translate }}</h3>
        <part-account-summary
          :summary="accountSummaries"
        ></part-account-summary>
      </div>
    </div>

    <div class="page-break-around no-print card card-primary mb-3">
      <div class="card-body">
        <h3>
          {{ "account_detail" | translate }} #{{ dailyReport.id }}
          <span class="text-muted">{{ "no_print" | translate }}</span>
        </h3>
        <div
          v-for="(list, index) in accountSummaries"
          :key="index"
          class="page-break-avoid"
        >
          <div v-if="list[0] !== 'merchandise'">
            <h4>{{ list[1][0]["accountType"] | translate }} @{{ list[0] }}</h4>
            <part-transaction-summary
              :transactions="list[1]"
            ></part-transaction-summary>
          </div>
        </div>
      </div>
    </div>

    <div v-if="dailyReport.reserveOrderItems.length" class="page-break-around">
      <h3>{{ "reserve_orders" | translate }} #{{ dailyReport.id }} [COPY]</h3>
      <h4>
        {{ "buyer" | translate }}: {{ dailyReport.buyer }} @
        {{ dailyReport.date }}
      </h4>

      <PartReserveOrderItemList
        :reserve-order-items="dailyReport.reserveOrderItems"
        :readonly="true"
      ></PartReserveOrderItemList>
    </div>
  </layout>
</template>

<script>
import FirebaseDailyReport from "@/services/FirebaseDailyReport";
import PartPurchaseOrderItemList from "./PartPurchaseOrderItemList";
import PartReserveOrderItemList from "./PartReserveOrderItemList";
import PartRefundOrderItemList from "./PartRefundOrderItemList";
import PartGiftCardOrderItemList from "./PartGiftCardOrderItemList";
import PartReceiveItemList from "./PartReceiveItemList";
import PartGiveItemList from "./PartGiveItemList";
import PartAccountSummary from "./PartAccountSummary";
import PartTransactionSummary from "./PartTransactionSummary";
import { ConfirmService } from "@/modules/xs-confirm";
import DailyReportService from "@/services/DailyReportService";
import moment from "moment";
import { mapState } from "vuex";

export default {
  data() {
    return {
      dailyReportId: this.$route.params["id"],
      dailyReport: null,
    };
  },

  computed: {
    accountSummaries() {
      return DailyReportService.groupTransactions(
        this.dailyReport.transactions
      );
    },
    isEditable() {
      if (this.adminMode) {
        return true;
      }

      return moment().isBefore(
        // last updated time + 3 hours as last time to edit
        moment(this.dailyReport.timestamp).add(3, "hour")
      );
    },

    ...mapState({
      adminMode: (state) => state.adminMode,
    }),
  },

  created() {
    this.loadData();
  },

  methods: {
    loadData() {
      if (!this.dailyReportId) {
        this.$router.push("/dailyReports");
      }
      FirebaseDailyReport.find(this.dailyReportId).then((doc) => {
        this.dailyReport = doc.data();
      });
    },

    remove() {
      ConfirmService.open(
        this.$translate.translate("delete_confirm_message")
      ).then((flag) => {
        if (!flag) {
          return;
        }

        FirebaseDailyReport.remove(this.dailyReportId);
        this.$router.push("/dailyReports");
      });
    },
    print() {
      window.print();
    },
  },
  components: {
    PartPurchaseOrderItemList,
    PartReserveOrderItemList,
    PartRefundOrderItemList,
    PartGiftCardOrderItemList,
    PartReceiveItemList,
    PartGiveItemList,
    PartTransactionSummary,
    PartAccountSummary,
  },
};
</script>
